import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { Search, Receipt, LayoutDashboard, BarChart3, Settings, Clock, ArrowRight, Clock3, Package, Bell, Users, FileText, Shield, Archive, Calendar, ToggleLeft, Tag, Megaphone, Key, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DataSyncService from '../services/DataSyncService';
import { usePermissions } from '../hooks/usePermissions';

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-12px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(4px);
  z-index: 99999;
  display: flex; justify-content: center;
  padding-top: 80px;
  animation: ${fadeIn} 0.15s ease;
`;

const Modal = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 16px;
  width: 600px;
  max-width: calc(100vw - 32px);
  max-height: 70vh;
  display: flex; flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0,0,0,0.3);
  animation: ${slideIn} 0.18s cubic-bezier(0.16,1,0.3,1);
`;

const InputRow = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  flex-shrink: 0;
`;

const Input = styled.input`
  flex: 1; background: none; border: none; outline: none;
  font-size: 16px; font-weight: 500;
  color: ${p => p.theme.colors.text}; font-family: inherit;
  &::placeholder { color: ${p => p.theme.colors.muted}; opacity: 0.5; }
`;

const CloseBtn = styled.button`
  background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'};
  border: none; border-radius: 6px; padding: 4px 8px;
  font-size: 11px; color: ${p => p.theme.colors.muted}; cursor: pointer; font-family: inherit;
`;

const Results = styled.div`overflow-y: auto; flex: 1; padding: 8px;`;
const Section = styled.div`margin-bottom: 4px;`;
const SectionLabel = styled.div`
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.06em; color: ${p => p.theme.colors.muted};
  padding: 8px 12px 4px;
`;

const ResultItem = styled.button`
  display: flex; align-items: center; gap: 12px;
  width: 100%; padding: 10px 12px;
  border: none;
  background: ${p => p.$active
    ? (p.theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')
    : 'transparent'};
  border-radius: 10px; cursor: pointer; text-align: left;
  transition: background 0.1s; font-family: inherit;
  &:hover { background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'}; }
`;

const ResultIcon = styled.div`
  width: 32px; height: 32px; border-radius: 8px;
  background: ${p => p.$bg || (p.theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)')};
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; color: ${p => p.$color || p.theme.colors.muted};
`;

const ResultText = styled.div`flex: 1; min-width: 0;`;
const ResultTitle = styled.div`
  font-size: 13px; font-weight: 600; color: ${p => p.theme.colors.text};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const ResultSub = styled.div`font-size: 11px; color: ${p => p.theme.colors.muted}; margin-top: 1px;`;
const ResultBadge = styled.div`
  font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px; flex-shrink: 0;
  background: ${p => STATUS_COLORS[p.$status]?.bg || 'rgba(0,0,0,0.06)'};
  color: ${p => STATUS_COLORS[p.$status]?.text || '#666'};
`;

const Empty = styled.div`
  padding: 48px 20px; text-align: center;
  color: ${p => p.theme.colors.muted}; font-size: 14px;
`;

const Footer = styled.div`
  padding: 10px 16px;
  border-top: 1px solid ${p => p.theme.colors.border};
  display: flex; gap: 16px; flex-shrink: 0;
`;
const FooterHint = styled.div`
  font-size: 11px; color: ${p => p.theme.colors.muted};
  display: flex; align-items: center; gap: 5px;
`;
const Kbd = styled.span`
  background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
  border-radius: 4px; padding: 1px 5px; font-size: 10px; font-family: monospace;
`;

const STATUS_LABELS = {
  nieuw: 'Nieuw',
  bereiding: 'Bereiding',
  klaar: 'Klaar',
  onderweg: 'Onderweg',
  bezorgd: 'Voltooid',
  voltooid: 'Voltooid',
  geannuleerd: 'Geannuleerd',
};

const STATUS_COLORS = {
  nieuw:       { bg: 'rgba(249,115,22,0.12)', text: '#f97316' },
  bereiding:   { bg: 'rgba(234,179,8,0.12)',  text: '#ca8a04' },
  klaar:       { bg: 'rgba(59,130,246,0.12)', text: '#3b82f6' },
  onderweg:    { bg: 'rgba(168,85,247,0.12)', text: '#a855f7' },
  bezorgd:     { bg: 'rgba(34,197,94,0.12)',  text: '#22c55e' },
  voltooid:    { bg: 'rgba(34,197,94,0.12)',  text: '#22c55e' },
  geannuleerd: { bg: 'rgba(239,68,68,0.1)',   text: '#ef4444' },
};

// permission: null = altijd toegankelijk, anders naam van permission of 'admin'
const ALL_PAGES = [
  // Hoofdnavigatie
  { id: 'dashboard',     label: 'Dashboard',            keywords: 'home overzicht',                                   path: '/KMFC/dashboard',                      icon: LayoutDashboard, color: '#f97316', group: 'Navigatie',    permission: null },
  { id: 'orders',        label: 'Bestellingen',          keywords: 'orders bestellingen nieuw actief',                 path: '/KMFC/orders',                         icon: Receipt,         color: '#3b82f6', group: 'Navigatie',    permission: 'can_view_new_orders' },
  { id: 'stats',         label: 'Statistieken',          keywords: 'stats omzet grafieken cijfers',                    path: '/KMFC/stats',                          icon: BarChart3,       color: '#8b5cf6', group: 'Navigatie',    permission: 'can_view_revenue_statistics' },
  { id: 'settings',      label: 'Instellingen',          keywords: 'instellingen beheer configuratie',                 path: '/KMFC/settings',                       icon: Settings,        color: '#6b7280', group: 'Navigatie',    permission: 'can_manage_settings' },

  // Sluitingen & openingstijden
  { id: 'opening-hours', label: 'Openingstijden',        keywords: 'openingstijden tijden uren open sluit',            path: '/KMFC/settings/opening-hours',         icon: Clock3,          color: '#f97316', group: 'Instellingen', permission: 'can_manage_settings' },
  { id: 'sluiting',      label: 'Sluiting instellen',    keywords: 'sluiting gesloten sluiten dicht noodsluit',        path: '/KMFC/settings/opening-hours?newSluiting=1', icon: ToggleLeft, color: '#ef4444', group: 'Instellingen', permission: 'can_manage_settings' },

  // Producten
  { id: 'product-status',label: 'Product status',        keywords: 'producten status beschikbaar uitverkocht',         path: '/KMFC/settings/product-status',        icon: Package,         color: '#22c55e', group: 'Instellingen', permission: 'can_change_product_status' },

  // Meldingen
  { id: 'notifications', label: 'Meldingen',             keywords: 'meldingen notificaties geluid pushbericht',        path: '/KMFC/settings/notifications',         icon: Bell,            color: '#eab308', group: 'Instellingen', permission: 'can_manage_settings' },

  // Gebruikers
  { id: 'users',         label: 'Gebruikers',            keywords: 'gebruikers medewerkers accounts rollen beheer',    path: '/KMFC/settings/users',                 icon: Users,           color: '#3b82f6', group: 'Instellingen', permission: 'admin' },

  // Account / beveiliging
  { id: 'account',       label: 'Account & beveiliging', keywords: 'account wachtwoord pincode profiel beveiliging',   path: '/KMFC/settings/account',               icon: Key,             color: '#8b5cf6', group: 'Instellingen', permission: null },

  // Overig
  { id: 'legal',         label: 'Juridisch',             keywords: 'juridisch privacy voorwaarden kvk',                path: '/KMFC/settings/legal',                 icon: FileText,        color: '#6b7280', group: 'Instellingen', permission: null },
];

function matchesQuery(order, q) {
  const lq = q.toLowerCase();
  return (
    order.customer_name?.toLowerCase().includes(lq) ||
    order.customer_phone?.toLowerCase().includes(lq) ||
    order.customer_email?.toLowerCase().includes(lq) ||
    order.order_code?.toLowerCase().includes(lq) ||
    order.delivery_address?.toLowerCase().includes(lq) ||
    order.delivery_city?.toLowerCase().includes(lq)
  );
}

function matchesPage(page, q) {
  const lq = q.toLowerCase();
  return (
    page.label.toLowerCase().includes(lq) ||
    page.keywords.toLowerCase().includes(lq)
  );
}

function formatCents(cents) {
  return `€ ${(cents / 100).toFixed(2).replace('.', ',')}`;
}

export default function SearchModal({ onClose }) {
  const [query, setQuery] = useState('');
  const [orderResults, setOrderResults] = useState([]);
  const [archiveResults, setArchiveResults] = useState([]);
  const [isSearchingArchive, setIsSearchingArchive] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const archiveTimer = useRef(null);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const allowedPages = ALL_PAGES.filter(p =>
    p.permission === null || hasPermission(p.permission)
  );

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (!query.trim()) {
      setOrderResults([]);
      setArchiveResults([]);
      setIsSearchingArchive(false);
      clearTimeout(archiveTimer.current);
      return;
    }

    const all = DataSyncService.getData('orders')?.all || [];
    const matched = all.filter(o => matchesQuery(o, query)).slice(0, 8);
    setOrderResults(matched);

    clearTimeout(archiveTimer.current);
    if (query.trim().length >= 2) {
      setIsSearchingArchive(true);
      archiveTimer.current = setTimeout(async () => {
        try {
          const results = await DataSyncService.searchArchive(query.trim());
          const cachedIds = new Set(matched.map(o => o.id));
          setArchiveResults(results.filter(o => !cachedIds.has(o.id)).slice(0, 5));
        } finally {
          setIsSearchingArchive(false);
        }
      }, 350);
    } else {
      setIsSearchingArchive(false);
    }

    return () => clearTimeout(archiveTimer.current);
  }, [query]);

  const trimmed = query.trim();

  // Pagina's: leeg = toon navigatie-items; met query = filter toegestane pagina's
  const pageMatches = !trimmed
    ? allowedPages.filter(p => p.group === 'Navigatie')
    : allowedPages.filter(p => matchesPage(p, trimmed));

  // Groepeer pagina's per group
  const pageGroups = pageMatches.reduce((acc, p) => {
    if (!acc[p.group]) acc[p.group] = [];
    acc[p.group].push(p);
    return acc;
  }, {});

  const allItems = [
    ...pageMatches.map(p => ({ type: 'page', data: p })),
    ...orderResults.map(o => ({ type: 'order', data: o })),
    ...archiveResults.map(o => ({ type: 'archive', data: o })),
  ];

  const go = useCallback((item) => {
    navigate(item.type === 'page' ? item.data.path : `/KMFC/orders`);
    onClose();
  }, [navigate, onClose]);

  useEffect(() => { setActiveIndex(0); }, [query]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, allItems.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); }
      if (e.key === 'Enter' && allItems[activeIndex]) { go(allItems[activeIndex]); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [allItems, activeIndex, go, onClose]);

  let itemIndex = 0;

  const hasResults = pageMatches.length > 0 || orderResults.length > 0 || archiveResults.length > 0;
  const showEmpty = trimmed && !hasResults && !isSearchingArchive;

  return ReactDOM.createPortal(
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <InputRow>
          <Search size={18} style={{ flexShrink: 0, opacity: 0.6, color: 'inherit' }} />
          <Input
            ref={inputRef}
            placeholder="Zoek bestellingen, klanten, instellingen..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <CloseBtn onClick={onClose}>Esc</CloseBtn>
        </InputRow>

        <Results>
          {/* Pagina's gegroepeerd */}
          {Object.entries(pageGroups).map(([group, pages]) => (
            <Section key={group}>
              <SectionLabel>{trimmed ? group : 'Snel navigeren'}</SectionLabel>
              {pages.map(page => {
                const idx = itemIndex++;
                const Icon = page.icon;
                return (
                  <ResultItem key={page.id} $active={activeIndex === idx} onClick={() => go({ type: 'page', data: page })}>
                    <ResultIcon $bg={`${page.color}18`} $color={page.color}><Icon size={15} /></ResultIcon>
                    <ResultText>
                      <ResultTitle>{page.label}</ResultTitle>
                      {trimmed && <ResultSub>{page.group}</ResultSub>}
                    </ResultText>
                    <ArrowRight size={13} style={{ opacity: 0.3 }} />
                  </ResultItem>
                );
              })}
            </Section>
          ))}

          {/* Bestellingen */}
          {orderResults.length > 0 && (
            <Section>
              <SectionLabel>Bestellingen</SectionLabel>
              {orderResults.map(order => {
                const idx = itemIndex++;
                return (
                  <ResultItem key={order.id} $active={activeIndex === idx} onClick={() => go({ type: 'order', data: order })}>
                    <ResultIcon><Receipt size={15} /></ResultIcon>
                    <ResultText>
                      <ResultTitle>{order.customer_name || 'Onbekend'}</ResultTitle>
                      <ResultSub>#{order.order_code} · {formatCents(order.total_cents)}</ResultSub>
                    </ResultText>
                    <ResultBadge $status={order.status}>{STATUS_LABELS[order.status] || order.status}</ResultBadge>
                  </ResultItem>
                );
              })}
            </Section>
          )}

          {/* Archief */}
          {archiveResults.length > 0 && (
            <Section>
              <SectionLabel>Archief</SectionLabel>
              {archiveResults.map(order => {
                const idx = itemIndex++;
                return (
                  <ResultItem key={order.id} $active={activeIndex === idx} onClick={() => go({ type: 'archive', data: order })}>
                    <ResultIcon><Clock size={15} /></ResultIcon>
                    <ResultText>
                      <ResultTitle>{order.customer_name || 'Onbekend'}</ResultTitle>
                      <ResultSub>#{order.order_code} · {formatCents(order.total_cents)}</ResultSub>
                    </ResultText>
                    <ResultBadge $status={order.status}>{STATUS_LABELS[order.status] || order.status}</ResultBadge>
                  </ResultItem>
                );
              })}
            </Section>
          )}

          {/* Alleen laad-indicator als er al resultaten zijn (orders gevonden maar archief nog laadt) */}
          {isSearchingArchive && orderResults.length > 0 && (
            <Section>
              <SectionLabel style={{ opacity: 0.4 }}>Archief wordt doorzocht...</SectionLabel>
            </Section>
          )}

          {showEmpty && <Empty>Geen resultaten voor "{query}"</Empty>}
        </Results>

        <Footer>
          <FooterHint><Kbd>↑↓</Kbd> navigeren</FooterHint>
          <FooterHint><Kbd>↵</Kbd> openen</FooterHint>
          <FooterHint><Kbd>Esc</Kbd> sluiten</FooterHint>
        </Footer>
      </Modal>
    </Overlay>,
    document.body
  );
}
